import React from 'react';
import { Users } from 'lucide-react';

export interface Cliente {
  id: string;
  nombres: string;
  apellidos: string;
  telefono: string;
  email: string;
  numeroDocumento?: string;
  direccion?: string;
  consentimientoDatos: boolean;
  creadoEn: string;
  actualizadoEn: string;
  estado: string;
}

interface ClientesTableProps {
  clientes: Cliente[];
  onClienteClick: (cliente: Cliente) => void;
  onEdit?: (cliente: Cliente) => void;
  onDelete?: (cliente: Cliente) => void;
  onToggleStatus?: (cliente: Cliente) => void;
  canEdit?: boolean;
  canDelete?: boolean;
  isLoading: boolean;
}

export const ClientesTable: React.FC<ClientesTableProps> = ({
  clientes,
  onClienteClick,
  onEdit,
  onDelete,
  onToggleStatus,
  canEdit,
  canDelete,
  isLoading
}) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getInitials = (nombres: string, apellidos: string) => {
    const firstName = nombres.split(' ')[0];
    const firstLastName = apellidos.split(' ')[0];
    return `${firstName.charAt(0)}${firstLastName.charAt(0)}`.toUpperCase();
  };

  const getRowClass = (cliente: Cliente) => {
    const hasMissingData = !cliente.email || !cliente.telefono;
    
    if (hasMissingData) {
      return "hover:bg-red-100 cursor-pointer transition-colors border-l-4 border-red-400 bg-red-50";
    }
    
    return "hover:bg-gray-50 cursor-pointer transition-colors";
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Cliente
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Contacto
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Teléfono
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Fecha de Registro
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Estado
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {Array.isArray(clientes) && clientes.map((cliente) => (
            <tr 
              key={cliente.id} 
              className={getRowClass(cliente)}
              onClick={() => onClienteClick(cliente)}
            >
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="shrink-0 h-10 w-10">
                    <div className="h-10 w-10 rounded-full bg-gradient-to-r from-emerald-400 to-teal-400 flex items-center justify-center">
                      <span className="text-white font-medium">
                        {getInitials(cliente.nombres, cliente.apellidos)}
                      </span>
                    </div>
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-gray-900">
                      {cliente.nombres} {cliente.apellidos}
                    </div>
                    <div className="text-sm text-gray-500">
                      ID: {cliente.id.slice(0, 8)}...
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">
                  {cliente.email || (
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                      ⚠ Email faltante
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-500">
                  {cliente.consentimientoDatos ? '✓ Consentimiento datos' : '✗ Sin consentimiento'}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {cliente.telefono ? (
                  <div className="text-sm text-gray-900">{cliente.telefono}</div>
                ) : (
                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                    ⚠ Faltante
                  </span>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {formatDate(cliente.creadoEn)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  cliente.estado === 'activo'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {cliente.estado}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Empty state */}
      {Array.isArray(clientes) && clientes.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <Users className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No hay clientes registrados</h3>
          <p className="mt-1 text-sm text-gray-500">Los clientes registrados aparecerán aquí.</p>
        </div>
      )}
    </div>
  );
};

export default ClientesTable;
